import React from 'react';
import { Drawer, Dialog, AppBar, Toolbar, IconButton, Box, List, ListItem, ListItemAvatar, Avatar, ListItemText, Button, Snackbar, Alert, Typography, useMediaQuery, DialogTitle, DialogContent, DialogContentText, DialogActions, Collapse, Link, Stack, Card, CardContent, CardActions, Chip, Divider } from '@mui/material';
import { Person as PersonIcon, Group, CheckCircle, HighlightOff, ExpandMore, ExpandLess, Close as CloseIcon, PeopleAlt, SendRounded, PersonAdd } from '@mui/icons-material';
import { getAvatarUrl } from '../../utils/avatar';
import {useTranslation} from "react-i18next";

export default function MyNetworkDrawer({ open, onClose, currentUser }) {
  function MessagePreview({ text, max = 160 }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = React.useState(false);
    if (!text) return null;
    const isLong = text.length > max;
    const shown = expanded || !isLong ? text : (text.slice(0, max) + '…');
    return (
      <Box sx={{ mt: 0.25, p: 1, borderRadius: 1.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)', border: '1px solid', borderColor: 'divider', maxWidth: '100%' }}>
        <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {shown}
        </Typography>
        {isLong && (
          <Box sx={{ mt: 0.5 }}>
            <Link component="button" type="button" onClick={() => setExpanded(v => !v)} sx={{ fontSize: 12 }}>
              {expanded ? t('header.friendConnections.showLess', 'Show less') : t('header.friendConnections.showMore', 'Show more')}
            </Link>
          </Box>
        )}
      </Box>
    );
  }
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [pending, setPending] = React.useState([]);
  const [sent, setSent] = React.useState([]);
  const [friends, setFriends] = React.useState([]);
  const [snack, setSnack] = React.useState(null);
  const [confirm, setConfirm] = React.useState({ open: false, friend: null, loading: false });
  const { t, i18n } = useTranslation();

  // Section toggles
  const [showPending, setShowPending] = React.useState(false);
  const [showSent, setShowSent] = React.useState(false);
  const [showFriends, setShowFriends] = React.useState(false);
  const hasInitFriendsToggle = React.useRef(false);
  const hasInitPendingToggle = React.useRef(false);
  const hasInitSentToggle = React.useRef(false);
  React.useEffect(() => {
    if (!hasInitFriendsToggle.current) {
      // Collapse by default when empty; expand if has items on first load
      setShowFriends(Array.isArray(friends) && friends.length > 0);
      hasInitFriendsToggle.current = true;
    }
  }, [friends]);
  React.useEffect(() => {
    if (!hasInitPendingToggle.current) {
      setShowPending(Array.isArray(pending) && pending.length > 0);
      hasInitPendingToggle.current = true;
    }
  }, [pending]);
  React.useEffect(() => {
    if (!hasInitSentToggle.current) {
      setShowSent(Array.isArray(sent) && sent.length > 0);
      hasInitSentToggle.current = true;
    }
  }, [sent]);

  const meId = String(currentUser?.id || currentUser?._id || 'me');

    const resolveAvatar = React.useCallback((u) => getAvatarUrl(u), []);

  const refresh = React.useCallback(async () => {
    // Try backend first
    const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
    const token = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null);
    try {
      const [pRes, sRes, fRes] = await Promise.all([
        fetch(`${API_BASE}/friends/requests/pending`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }),
        fetch(`${API_BASE}/friends/requests/sent`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }),
        fetch(`${API_BASE}/friends/list`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} })
      ]);
      if (pRes.ok) {
        const pdata = await pRes.json();
        setPending(Array.isArray(pdata?.requests) ? pdata.requests : (Array.isArray(pdata) ? pdata : []));
      } else {
        // backend only: no mock fallback for pending
        setPending([]);
      }
      if (sRes.ok) {
        const sdata = await sRes.json();
        setSent(Array.isArray(sdata?.requests) ? sdata.requests : (Array.isArray(sdata) ? sdata : []));
      } else {
        setSent([]);
      }
      if (fRes.ok) {
        const fdata = await fRes.json();
        const newFriends = Array.isArray(fdata?.friends) ? fdata.friends : (Array.isArray(fdata) ? fdata : []);
        setFriends(newFriends);
        try {
          const mapped = newFriends.map(f => ({ id: String(f.id || f._id), name: f.name || f.username || f.email || 'User', avatar: f.avatarUrl || f.avatar || null }));
          window.dispatchEvent(new CustomEvent('chat:friendsUpdate', { detail: { friends: mapped } }));
        } catch (_) {}
      } else {
        setFriends([]);
      }
    } catch {
      // Backend only: on error, show nothing rather than mock
      setPending([]);
      setSent([]);
      setFriends([]);
    }
  }, [meId]);

  React.useEffect(() => {
    refresh();
    const onRealtime = () => refresh();
    try { window.addEventListener('friends:changed', onRealtime); } catch {}
    return () => {
      try { window.removeEventListener('friends:changed', onRealtime); } catch {}
    };
  }, [currentUser, refresh]);

  const handleAction = async (reqId, action, fromName) => {
    // Try backend respond first
    const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
    const token = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null);
    try {
      const res = await fetch(`${API_BASE}/friends/requests/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ requestId: reqId, action })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Action failed');
      if (action === 'approve') setSnack({ type: 'success', msg: t('network.requestApproved', 'You are now friends with {{name}}!', { name: fromName }) });
      if (action === 'reject') setSnack({ type: 'info', msg: t('network.requestRejected', 'Request rejected.') });
      refresh();
      try { window.dispatchEvent(new Event('friends:changed')); } catch {}
      return;
    } catch (_) {
      // backend only: show error
      setSnack({ type: 'error', msg: t('network.actionFailed', 'Action failed.') });
    }
  };

  const handleCancel = async (id) => {
    const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
    const token = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null);
    try {
      const res = await fetch(`${API_BASE}/friends/requests/${id}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed');
      setSnack({ type: 'info', msg: t('network.requestCancelled', 'Friend request cancelled.') });
      refresh();
      try { window.dispatchEvent(new Event('friends:changed')); } catch {}
    } catch (e) {
      setSnack({ type: 'error', msg: t('network.actionFailed', 'Action failed.') });
    }
  };

  // Poll when drawer is open for near-real-time updates
  React.useEffect(() => {
    if (!open) return; 
    refresh();
    let timer = null;
    try { timer = setInterval(() => refresh(), 5000); } catch {}
    return () => { if (timer) try { clearInterval(timer); } catch {} };
  }, [open, refresh]);

  const Content = (
    <Box sx={{ width: '100%' }}>
      {!isMobile && (
        <Box sx={{
          p: 2,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              width: 40,
              height: 40,
              backdropFilter: 'blur(10px)'
            }}>
              <PeopleAlt sx={{ color: 'white', fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
                {t('header.friendConnections.tittle', 'Bạn bè & Kết nối')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {friends.length} {t('header.friendConnections.friendsCount', 'bạn bè')}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}
      <Box sx={{ px: 1.5, pt: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.1) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.2)'
          }}>
            <PeopleAlt sx={{ color: '#10b981', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, color: '#059669', fontSize: '0.85rem' }}>
              {t('header.friendConnections.friendsList', 'Bạn bè')}
            </Typography>
            <Chip
              label={friends.length}
              size="small"
              sx={{
                height: 20,
                minWidth: 20,
                bgcolor: '#10b981',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.7rem',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={() => setShowFriends(v => !v)}
            sx={{
              bgcolor: 'rgba(16, 185, 129, 0.1)',
              '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.2)' }
            }}
          >
            {showFriends ? <ExpandLess sx={{ color: '#10b981' }} /> : <ExpandMore sx={{ color: '#10b981' }} />}
          </IconButton>
        </Stack>
      </Box>
      <Collapse in={showFriends} timeout="auto" unmountOnExit>
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        {friends.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{
            py: 4,
            px: 2,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(52, 211, 153, 0.05) 100%)',
            border: '1px dashed rgba(16, 185, 129, 0.3)'
          }}>
            <Avatar sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
              width: 56,
              height: 56,
              boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)'
            }}>
              <PeopleAlt sx={{ fontSize: 28, color: 'white' }} />
            </Avatar>
            <Typography variant="body2" sx={{ color: '#059669', fontWeight: 500, textAlign: 'center' }}>
              {t('header.friendConnections.noFriends', 'Bạn chưa có bạn nào.')}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1}>
            {friends.map((fr) => (
              <Box
                key={fr.id || fr._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  borderRadius: 2.5,
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(16, 185, 129, 0.08)'
                    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.04) 0%, rgba(52, 211, 153, 0.04) 100%)',
                  border: '1px solid',
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateX(4px)',
                    borderColor: '#10b981',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)'
                  }
                }}
              >
                <Avatar
                  src={resolveAvatar(fr)}
                  sx={{
                    width: 42,
                    height: 42,
                    border: '2px solid #10b981',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                  }}
                >
                  {!resolveAvatar(fr) && <PersonIcon />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.3 }}>
                    {fr.name || fr.username || fr.email}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {fr.major || '—'} • {fr.school || '—'}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setConfirm({ open: true, friend: fr, loading: false })}
                  sx={{
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  {t('network.removeFriend', 'Xóa')}
                </Button>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
      </Collapse>

      <Box sx={{ px: 1.5, pt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.1) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}>
            <SendRounded sx={{ color: '#3b82f6', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, color: '#2563eb', fontSize: '0.85rem' }}>
              {t('header.friendConnections.sentRequests', 'Đã gửi')}
            </Typography>
            <Chip
              label={sent.length}
              size="small"
              sx={{
                height: 20,
                minWidth: 20,
                bgcolor: '#3b82f6',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.7rem',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={() => setShowSent(v => !v)}
            sx={{
              bgcolor: 'rgba(59, 130, 246, 0.1)',
              '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.2)' }
            }}
          >
            {showSent ? <ExpandLess sx={{ color: '#3b82f6' }} /> : <ExpandMore sx={{ color: '#3b82f6' }} />}
          </IconButton>
        </Stack>
      </Box>
      <Collapse in={showSent} timeout="auto" unmountOnExit>
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        {sent.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{
            py: 4,
            px: 2,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(96, 165, 250, 0.05) 100%)',
            border: '1px dashed rgba(59, 130, 246, 0.3)'
          }}>
            <Avatar sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              width: 56,
              height: 56,
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)'
            }}>
              <SendRounded sx={{ fontSize: 28, color: 'white' }} />
            </Avatar>
            <Typography variant="body2" sx={{ color: '#2563eb', fontWeight: 500, textAlign: 'center' }}>
              {t('header.friendConnections.noSentRequest', 'Bạn chưa gửi lời mời kết bạn nào.')}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1}>
            {sent.map((req) => (
              <Box
                key={req.id}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(59, 130, 246, 0.08)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.04) 0%, rgba(96, 165, 250, 0.04) 100%)',
                  border: '1px solid',
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateX(4px)',
                    borderColor: '#3b82f6',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
                  }
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    src={resolveAvatar(req.toUser)}
                    sx={{
                      width: 44,
                      height: 44,
                      border: '2px solid #3b82f6',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
                    }}
                  >
                    {!resolveAvatar(req.toUser) && <PersonIcon />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>
                      {req.toUser?.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {req.toUser?.major} • {req.toUser?.school}
                    </Typography>
                  </Box>
                </Stack>
                {req.message && (
                  <Box sx={{ mt: 1 }}>
                    <MessagePreview text={req.message} />
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<HighlightOff sx={{ fontSize: 16 }} />}
                    onClick={() => handleCancel(req.id)}
                    sx={{
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.4,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    {t('network.cancel', 'Hủy')}
                  </Button>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
      </Collapse>

      <Box sx={{ px: 1.5, pt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(167, 139, 250, 0.1) 100%)',
            border: '1px solid rgba(139, 92, 246, 0.2)'
          }}>
            <PersonAdd sx={{ color: '#8b5cf6', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, color: '#7c3aed', fontSize: '0.85rem' }}>
              {t('header.friendConnections.friendRequests', 'Lời mời')}
            </Typography>
            {pending.length > 0 && (
              <Chip
                label={pending.length}
                size="small"
                sx={{
                  height: 20,
                  minWidth: 20,
                  bgcolor: '#8b5cf6',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.1)' }
                  },
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={() => setShowPending(v => !v)}
            sx={{
              bgcolor: 'rgba(139, 92, 246, 0.1)',
              '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.2)' }
            }}
          >
            {showPending ? <ExpandLess sx={{ color: '#8b5cf6' }} /> : <ExpandMore sx={{ color: '#8b5cf6' }} />}
          </IconButton>
        </Stack>
      </Box>
      <Collapse in={showPending} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.5, pb: 2 }}>
        {pending.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{
            py: 4,
            px: 2,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(167, 139, 250, 0.05) 100%)',
            border: '1px dashed rgba(139, 92, 246, 0.3)'
          }}>
            <Avatar sx={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
              width: 56,
              height: 56,
              boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)'
            }}>
              <PersonAdd sx={{ fontSize: 28, color: 'white' }} />
            </Avatar>
            <Typography variant="body2" sx={{ color: '#7c3aed', fontWeight: 500, textAlign: 'center' }}>
              {t('header.friendConnections.noPendingRequest', 'Hiện chưa có lời mời kết bạn nào.')}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {pending.map((req) => (
              <Box
                key={req.id}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(139, 92, 246, 0.08)'
                    : 'linear-gradient(135deg, rgba(139, 92, 246, 0.06) 0%, rgba(167, 139, 250, 0.06) 100%)',
                  border: '1px solid',
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateX(4px)',
                    borderColor: '#8b5cf6',
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.15)'
                  }
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    src={resolveAvatar(req.fromUser)}
                    sx={{
                      width: 48,
                      height: 48,
                      border: '2px solid #8b5cf6',
                      boxShadow: '0 2px 8px rgba(139, 92, 246, 0.25)'
                    }}
                  >
                    {!resolveAvatar(req.fromUser) && <PersonIcon />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }}>
                      {req.fromUser?.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {req.fromUser?.major} • {req.fromUser?.school}
                    </Typography>
                  </Box>
                </Stack>
                {req.message && (
                  <Box sx={{ mt: 1 }}>
                    <MessagePreview text={req.message} />
                  </Box>
                )}
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                    onClick={() => handleAction(req.id, 'approve', req.fromUser?.name)}
                    sx={{
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                      }
                    }}
                  >
                    {t('common.approve', 'Chấp nhận')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<HighlightOff sx={{ fontSize: 16 }} />}
                    onClick={() => handleAction(req.id, 'reject', req.fromUser?.name)}
                    sx={{
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    {t('common.reject', 'Từ chối')}
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
        </Box>
      </Collapse>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Dialog fullScreen open={open} onClose={onClose}>
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
              pt: 'env(safe-area-inset-top, 0px)'
            }}
          >
            <Toolbar sx={{ minHeight: { xs: 56 }, px: { xs: 1 } }}>
              <IconButton
                edge="start"
                onClick={onClose}
                aria-label="Close"
                sx={{ width: 40, height: 40, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
              >
                <CloseIcon />
              </IconButton>
              <Stack sx={{ ml: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
                  {t('header.friendConnections.tittle', 'Bạn bè & Kết nối')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {friends.length} {t('header.friendConnections.friendsCount', 'bạn bè')}
                </Typography>
              </Stack>
            </Toolbar>
          </AppBar>
          <Box sx={{ flex: 1, overflowY: 'auto', pb: 'env(safe-area-inset-bottom, 0px)' }}>
            {Content}
          </Box>
        </Dialog>
      ) : (
        <Drawer
          anchor="right"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 400 },
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              border: 'none',
              boxShadow: '0 8px 32px rgba(99, 102, 241, 0.15)',
              height: '100vh',
              display: 'flex',
              overflow: 'hidden'
            }
          }}
        >
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {Content}
          </Box>
        </Drawer>
      )}
      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.type} sx={{ width: '100%' }}>{snack.msg}</Alert> : null}
      </Snackbar>

      {/* Confirm remove friend dialog */}
      <Dialog
        open={confirm.open}
        onClose={() => !confirm.loading && setConfirm({ open: false, friend: null, loading: false })}
        PaperProps={{
          sx: { borderRadius: 3, minWidth: 320 }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
          {t('network.removeFriend', 'Xóa bạn bè')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            {t('network.confirmRemoveFriend', 'Xóa {{name}} khỏi danh sách bạn bè?', { name: confirm.friend?.name || confirm.friend?.username || confirm.friend?.email || '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirm({ open: false, friend: null, loading: false })}
            disabled={confirm.loading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.cancel', 'Hủy')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={confirm.loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
            }}
            onClick={async () => {
            const fr = confirm.friend;
            if (!fr) return;
            setConfirm(prev => ({ ...prev, loading: true }));
            const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
            const token = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null);
            try {
              const res = await fetch(`${API_BASE}/friends/${fr.id || fr._id}`, {
                method: 'DELETE',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
              });
              const data = await res.json().catch(() => ({}));
              if (!res.ok) throw new Error(data?.error || 'Failed');
              setSnack({ type: 'success', msg: t('network.friendRemoved', 'Removed from friends.') });
              setConfirm({ open: false, friend: null, loading: false });
              refresh();
              try { window.dispatchEvent(new Event('friends:changed')); } catch {}
            } catch (e) {
              setSnack({ type: 'error', msg: t('network.actionFailed', 'Action failed.') });
              setConfirm(prev => ({ ...prev, loading: false }));
            }
          }}>{t('common.confirm', 'Xác nhận')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
